/**
 * Parses Google Place Details address_components into structured fields
 * for return address (address1, address2, city, state, zip).
 */

import type { ParsedAddressFields } from "./type";

type AddressComponents = google.maps.places.PlaceResult["address_components"];

function getComponent(
  components: AddressComponents | undefined,
  type: string
): { long_name: string; short_name: string } | undefined {
  if (!components) return undefined;
  return components.find((c) => c.types?.includes(type));
}

export type { ParsedAddressFields };

/**
 * Maps Place Details address_components to return address fields.
 * Handles US-style and common international components.
 */
export function parsePlaceDetailsToAddressFields(
  placeDetails: google.maps.places.PlaceResult | null | undefined
): ParsedAddressFields {
  const empty: ParsedAddressFields = {
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
  };
  const components = placeDetails?.address_components;
  if (!components?.length) return empty;

  const streetNumber = getComponent(components, "street_number");
  const route = getComponent(components, "route");
  const subpremise = getComponent(components, "subpremise");
  const locality = getComponent(components, "locality");
  const admin1 = getComponent(components, "administrative_area_level_1");
  const postalCode = getComponent(components, "postal_code");
  const postalTown = getComponent(components, "postal_town");
  const admin2 = getComponent(components, "administrative_area_level_2");

  const address1Parts = [streetNumber?.long_name, route?.long_name].filter(Boolean);
  const address1 = address1Parts.length ? address1Parts.join(" ") : route?.long_name ?? "";

  const address2 = subpremise?.long_name ?? "";

  const city = locality?.long_name ?? postalTown?.long_name ?? admin2?.long_name ?? "";

  const state = admin1?.short_name ?? admin1?.long_name ?? "";

  const zip = postalCode?.long_name ?? "";

  return { address1, address2, city, state, zip };
}
